import React from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import axios from 'axios'

import { Dialog, Loader, PercentButton, CellStatus } from '@percent/admin-dashboard/common/components'
import { Box, Text } from '@percent/lemonade'
import { useMutation } from '@percent/admin-dashboard/common/hooks'
import { FieldDropDown } from '@percent/admin-dashboard/common/components/fieldDropDown/FieldDropDown'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useToast } from '@percent/admin-dashboard/containers/toast/ToastContext'
import { ComplianceEventType, VettingEventScope, VettingStatus } from '@percent/admin-dashboard/api/types'
import styles from './EditComplianceModal.module.scss'
import { EditComplianceModalProps } from './EditComplianceModal.types'
import { LocaleKey } from '@percent/admin-dashboard/i18n/config'

const complianceEvents: ComplianceEventType[] = [
  'adverseMedia',
  'sanction',
  'warning',
  'commercial',
  'controversial',
  'hateSpeech'
]

const eventStatuses: { title: LocaleKey; value: VettingStatus }[] = [
  {
    title: 'status.yes',
    value: VettingStatus.YES
  },
  {
    title: 'status.no',
    value: VettingStatus.NO
  }
]

const MISSING_PENDING_EVENT_ERROR_CODE = 'monitoring/missing_flag_for_pending_event'

export function EditComplianceModal({
  isOpened,
  setIsOpened,
  vettingEventsResults,
  refresh,
  refreshVettingEventsResults,
  organisationId,
  organisationName
}: EditComplianceModalProps) {
  const { t } = useTranslation()
  const { adminService } = useServices()
  const addToast = useToast()

  const onSuccess = () => {
    addToast(t('typography.changesSaved'), 'success')
    setIsOpened(false)
    refresh()
    refreshVettingEventsResults?.()
  }

  const onError = (err: Error) => {
    if (axios.isAxiosError(err)) {
      if (err?.response?.data?.error?.code === MISSING_PENDING_EVENT_ERROR_CODE) {
        addToast(t('errorMessage.missingPendingEvent', { orgName: organisationName }), 'error')
      } else {
        addToast(t('errorMessage.genericError'), 'error')
      }
    }

    setIsOpened(false)
    refresh()
    refreshVettingEventsResults?.()
  }

  const [{ isLoading: isSubmissionLoading }, { apiFunc: submitFunc }] = useMutation(
    adminService.submitVettingEvents,
    onSuccess,
    onError
  )

  const { values, setFieldValue, isValid, dirty } = useFormik({
    initialValues: {
      event: '',
      status: ''
    },
    validationSchema: () =>
      yup.object().shape({
        event: yup.mixed().oneOf(complianceEvents).required('Required').defined(),
        status: yup.string().required('Required')
      }),
    onSubmit: () => {}
  })

  const { event, status } = values

  const handleSubmit = async () =>
    submitFunc({
      organisationId,
      payload: { flags: [{ scope: event as VettingEventScope, status: status as VettingStatus }] }
    })

  const complianceModalView = (
    <>
      <Box className={styles.modalForm}>
        <FieldDropDown
          selectTitle={t('form.selectEvent')}
          placeholder={t('form.selectEventPlaceholder')}
          initialValue={event}
          onClick={(
            e: React.ChangeEvent<{
              name?: string
              value: unknown
            }>
          ) => {
            setFieldValue('event', e.target.value)
            setFieldValue('status', '')
          }}
          valueArray={complianceEvents.map(complianceEvent => {
            return { title: t(`form.${complianceEvent}`), value: complianceEvent }
          })}
        />
      </Box>
      {event && (
        <Box className={styles.modalForm}>
          <Box className={styles.currentStatus}>
            <Text size="small">{t('form.currentStatus')}</Text>
            <CellStatus status={vettingEventsResults.results?.[event]?.status || VettingStatus.PENDING} />
          </Box>
        </Box>
      )}
      <Box className={styles.modalForm}>
        <FieldDropDown
          selectTitle={t('form.selectNewStatus')}
          placeholder={t('form.selectStatus')}
          initialValue={status}
          onClick={(
            e: React.ChangeEvent<{
              name?: string
              value: unknown
            }>
          ) => setFieldValue('status', e.target.value)}
          valueArray={eventStatuses.map(eventStatus => {
            return { title: t(eventStatus.title), value: eventStatus.value }
          })}
        />
      </Box>
      <Box className={styles.dialogFooter}>
        <PercentButton
          disabled={!(isValid && dirty)}
          title={t('button.edit')}
          onClick={handleSubmit}
          testId="submit-vetting-events-button"
        />
        <PercentButton
          title={t('button.cancel')}
          onClick={() => setIsOpened(false)}
          textColour="secondaryTextColour"
          outlined
        />
      </Box>
    </>
  )

  return (
    <Dialog openModal={isOpened} onClose={() => setIsOpened(false)} headerTitle={t('dialog.editComplianceEvent')}>
      {isSubmissionLoading ? <Loader /> : complianceModalView}
    </Dialog>
  )
}
